<template>
  <div
    class="invoice-preview-root"
    :class="{ 'invoice-preview-root--offscreen': offscreen }"
  >
    <div ref="captureTarget" class="invoice-preview">
      <!-- 顶部：二维码、标题、发票号码 -->
      <div class="invoice-preview__top">
        <div class="invoice-preview__top-left">
          <div class="invoice-preview__qr" aria-hidden="true">
            <div class="invoice-preview__qr-inner">
              <!-- <span class="invoice-preview__qr-tax">税</span> -->
            </div>
          </div>
          <span class="invoice-preview__oil-type"></span>
        </div>

        <div class="invoice-preview__top-center">
          <div class="invoice-preview__title-wrap">
            <h1 class="invoice-preview__title">{{ invoiceTitle }}</h1>
            <div class="invoice-preview__title-lines" aria-hidden="true">
              <span class="invoice-preview__title-line"></span>
              <span class="invoice-preview__title-line"></span>
            </div>
          </div>
          <div class="invoice-preview__seal">
            <span
              class="invoice-preview__seal-text invoice-preview__seal-text--top"
              >国家税务总局</span
            >
            <span
              class="invoice-preview__seal-text invoice-preview__seal-text--bottom"
              >湖北省税务局</span
            >
          </div>
        </div>

        <div class="invoice-preview__top-right">
          <div>
            <span class="is-label">发票号码：</span
            ><span class="is-value">{{ invoiceNo }}</span>
          </div>
          <div>
            <span class="is-label">开票日期：</span
            ><span class="is-value"></span>
          </div>
        </div>
      </div>

      <!-- 主体表格 -->
      <div class="invoice-preview__sheet">
        <!-- 购销方信息 -->
        <div class="invoice-preview__party">
          <div class="invoice-preview__party-block">
            <div class="invoice-preview__party-label">购买方信息</div>
            <div class="invoice-preview__party-content">
              <p  style="margin-bottom: 20px;">
                <span class="is-label">名称：</span
                ><span class="is-value">{{ buyerName }}</span>
              </p>
              <p>
                <span class="is-label">统一社会信用代码/纳税人识别号：</span
                ><span class="is-value">{{ buyerTaxNo }}</span>
              </p>
            </div>
          </div>
          <div class="invoice-preview__party-block">
            <div class="invoice-preview__party-label">销售方信息</div>
            <div class="invoice-preview__party-content">
              <p style="margin-bottom: 20px;">
                <span class="is-label">名称：</span
                ><span class="is-value">{{ sellerName }}</span>
              </p>
              <p>
                <span class="is-label">统一社会信用代码/纳税人识别号：</span
                ><span class="is-value">{{ sellerTaxNo }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- 明细表 -->
        <table class="invoice-preview__table" cellspacing="0" cellpadding="0">
          <colgroup>
            <col class="invoice-preview__col-name" />
            <col class="invoice-preview__col-spec" />
            <col class="invoice-preview__col-unit" />
            <col class="invoice-preview__col-qty" />
            <col class="invoice-preview__col-price" />
            <col class="invoice-preview__col-amount" />
            <col class="invoice-preview__col-rate" />
            <col class="invoice-preview__col-tax" />
          </colgroup>
          <thead>
            <tr>
              <th>项目名称</th>
              <th>规格型号</th>
              <th>单位</th>
              <th>数量</th>
              <th>单价</th>
              <th>金额</th>
              <th>税率/征收率</th>
              <th>税额</th>
            </tr>
          </thead>
          <tbody>
            <tr class="invoice-preview__item-row">
              <td>{{ itemName }}</td>
              <td>{{ itemSpec }}</td>
              <td>{{ itemUnit }}</td>
              <td class="is-num">{{ itemQty }}</td>
              <td class="is-num">{{ itemPrice }}</td>
              <td class="is-num">{{ itemAmount }}</td>
              <td class="is-num">{{ taxRateText }}</td>
              <td class="is-num">{{ itemTax }}</td>
            </tr>
            <tr class="invoice-preview__blank-row">
              <td colspan="8"></td>
            </tr>
            <tr class="invoice-preview__sum-row">
              <td colspan="2" class="invoice-preview__sum-label">
                合&nbsp;&nbsp;计
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td class="is-num">¥{{ itemAmount }}</td>
              <td></td>
              <td class="is-num">¥{{ itemTax }}</td>
            </tr>
          </tbody>
        </table>

        <!-- 价税合计 -->
        <div class="invoice-preview__total">
          <div class="invoice-preview__total-label">价税合计（大写）</div>
          <div class="invoice-preview__total-words is-value">
            <span class="invoice-preview__total-icon" aria-hidden="true"
              >⊗</span
            >
            {{ totalChinese }}
          </div>
          <div class="invoice-preview__total-num">
            <span class="is-label">（小写）</span
            ><span class="is-value">¥{{ totalAmount }}</span>
          </div>
        </div>

        <!-- 备注 -->
        <div class="invoice-preview__remark">
          <div class="invoice-preview__remark-label">备&nbsp;&nbsp;注</div>
          <div class="invoice-preview__remark-content">
            <p v-for="(line, index) in remarkLines" :key="index">{{ line }}</p>
          </div>
        </div>
      </div>

      <!-- 底部开票人 -->
      <div class="invoice-preview__issuer">
        <span class="is-label">开票人：</span><span class="is-value">{{ issuer }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import {
  splitTaxInclusive,
  amountToChinese,
} from "./invoicePreviewUtils";

/** 销售方固定信息（开票主体） */
const SELLER = {
  name: "武汉杰宜斯信息科技有限公司",
  taxNo: "91420106303306744J",
};

/** 软件服务默认税率 */
const DEFAULT_TAX_RATE = 0.06;

export default {
  name: "InvoicePreview",
  props: {
    /** 离屏渲染，供截图使用 */
    offscreen: {
      type: Boolean,
      default: false,
    },
    /** 发票预览数据，由申请弹窗表单与订单组装 */
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    /** 专票 invoiceCategory=1 */
    isSpecialInvoice() {
      return Number(this.data.invoiceCategory) === 1;
    },
    invoiceTitle() {
      return this.isSpecialInvoice
        ? "电子发票（增值税专用发票）"
        : "电子发票（普通发票）";
    },
    invoiceNo() {
      return this.data.invoiceNo || "";
    },
    buyerName() {
      return String(this.data.title || "").trim();
    },
    buyerTaxNo() {
      if (Number(this.data.invoiceType) !== 1) return "";
      return String(this.data.taxNo || "").trim();
    },
    sellerName() {
      return SELLER.name;
    },
    sellerTaxNo() {
      return SELLER.taxNo;
    },
    taxParts() {
      return splitTaxInclusive(this.data.amount, DEFAULT_TAX_RATE);
    },
    itemName() {
      const service = String(this.data.serviceName || "软件服务费").trim();
      return `*生产生活服务*信息技术服务`;
      // ${service}
    },
    itemSpec() {
      return this.data.itemSpec || "";
    },
    itemUnit() {
      return this.data.itemUnit || "套";
    },
    itemQty() {
      return this.data.itemQty != null ? this.data.itemQty : 1;
    },
    itemPrice() {
      return this.taxParts.amount;
    },
    itemAmount() {
      return this.taxParts.amount;
    },
    itemTax() {
      return this.taxParts.tax;
    },
    taxRateText() {
      return `${Math.round(DEFAULT_TAX_RATE * 100)}%`;
    },
    totalAmount() {
      return this.taxParts.total;
    },
    totalChinese() {
      return amountToChinese(this.totalAmount);
    },
    /** 备注：专票显示企业四要素，普票显示收票邮箱 */
    remarkLines() {
      if (this.isSpecialInvoice) {
        return [
          ["注册地址", this.data.address],
          ["联系电话", this.data.phone],
          ["开户行", this.data.bankName],
          ["银行账号", this.data.bankAccount],
        ].map(([label, value]) => `${label}：${String(value || "").trim()}`);
      }
      const email = String(this.data.email || "").trim();
      return email ? [`收票邮箱：${email}`] : [];
    },
    issuer() {
      return this.data.issuer || "";
    },
  },
  methods: {
    /** 将发票 DOM 转为 PNG 图片 data URL */
    captureImage() {
      const el = this.$refs.captureTarget;
      if (!el) {
        return Promise.reject(new Error("发票预览节点不存在"));
      }
      return html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      }).then((canvas) => canvas.toDataURL("image/png"));
    },
  },
};
</script>

<style scoped>
.invoice-preview-root--offscreen {
  position: fixed;
  left: -10000px;
  top: 0;
  z-index: -1;
  pointer-events: none;
}

.invoice-preview {
  --invoice-primary: #800000;
  --invoice-data: #000000;
  --invoice-border: 1px solid var(--invoice-primary);
  width: 860px;
  padding: 20px;
  font-family: "SimSun", "Songti SC", "STSong", serif;
  font-size: 12px;
  line-height: 1.4;
  color: var(--invoice-data);
  background: #fff;
  box-sizing: border-box;
  user-select: none;
}

/* 栏目标题 13px / 信息内容 12px；颜色：标签 #800000，内容 #000000 */
.invoice-preview .is-label {
  font-size: 13px;
  color: var(--invoice-primary);
}

.invoice-preview .is-value {
  font-size: 12px;
  color: var(--invoice-data);
}

/* ===== 顶部区域 ===== */
.invoice-preview__top {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  margin-bottom: 6px;
  min-height: 80px;
}

.invoice-preview__top-left {
  position: relative;
  z-index: 1;
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
}

.invoice-preview__qr {
  width: 72px;
  height: 72px;
  border: 1px solid #ccc;
  background: repeating-linear-gradient(
      0deg,
      #222 0,
      #222 2px,
      #fff 2px,
      #fff 4px
    ),
    repeating-linear-gradient(90deg, #222 0, #222 2px, #fff 2px, #fff 4px);
  background-blend-mode: multiply;
  position: relative;
  flex-shrink: 0;
}

.invoice-preview__qr-inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.invoice-preview__qr-tax {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: #e8a317;
  border-radius: 2px;
  font-family: "Microsoft YaHei", sans-serif;
}

.invoice-preview__oil-type {
  margin-top: 4px;
  font-size: 12px;
  letter-spacing: 1px;
  color: var(--invoice-data);
}

.invoice-preview__top-center {
  grid-column: 1 / -1;
  grid-row: 1;
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 80px;
  padding: 0;
  pointer-events: none;
}

.invoice-preview__title-wrap {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: max-content;
  max-width: 100%;
  margin-top: 8px;
}

.invoice-preview__title {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: 2px;
  color: var(--invoice-primary);
  font-family: "KaiTi", "STKaiti", "SimKai", serif;
  white-space: nowrap;
  line-height: 1.2;
  text-align: center;
}

.invoice-preview__title-lines {
  width: 100%;
  margin-top: 4px;
}

.invoice-preview__title-line {
  display: block;
  width: 100%;
  height: 0;
  border-top: 1px solid var(--invoice-primary);
}

.invoice-preview__title-line + .invoice-preview__title-line {
  margin-top: 2px;
}

.invoice-preview__seal {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 66px;
  border: 2px solid var(--invoice-primary);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0.75;
  pointer-events: none;
}

.invoice-preview__seal-text {
  font-size: 13px;
  font-weight: 700;
  color: var(--invoice-primary);
  letter-spacing: 2px;
  line-height: 1.5;
  font-family: "SimSun", serif;
}

.invoice-preview__top-right {
  position: relative;
  z-index: 1;
  grid-column: 3;
  grid-row: 1;
  flex-shrink: 0;
  min-width: 200px;
  justify-self: end;
  text-align: left;
  line-height: 1.8;
  padding-top: 4px;
  white-space: nowrap;
}

/* ===== 主体边框区域 ===== */
.invoice-preview__sheet {
  border: var(--invoice-border);
}

/* 购销方 */
.invoice-preview__party {
  display: flex;
  border-bottom: var(--invoice-border);
}

.invoice-preview__party-block {
  flex: 1 1 50%;
  max-width: 50%;
  display: flex;
  min-height: 72px;
  min-width: 0;
}

.invoice-preview__party-block + .invoice-preview__party-block {
  border-left: var(--invoice-border);
}

.invoice-preview__party-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex-shrink: 0;
  border-right: var(--invoice-border);
  font-size: 13px;
  letter-spacing: 4px;
  padding: 4px 0;
  color: var(--invoice-primary);
}

.invoice-preview__party-content {
  flex: 1;
  min-width: 0;
  width: 382px;
  max-width: 100%;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.9;
  box-sizing: border-box;
}

.invoice-preview__party-content p {
  margin: 0;
}

.invoice-preview__party-content .is-label {
  white-space: nowrap;
}

.invoice-preview__party-content .is-value {
  white-space: normal;
  word-break: break-all;
}

/* 明细表 */
.invoice-preview__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.invoice-preview__col-name {
  width: 28%;
}
.invoice-preview__col-spec {
  width: 9%;
}
.invoice-preview__col-unit {
  width: 5%;
}
.invoice-preview__col-qty {
  width: 8%;
}
.invoice-preview__col-price {
  width: 14%;
}
.invoice-preview__col-amount {
  width: 10%;
}
.invoice-preview__col-rate {
  width: 12%;
}
.invoice-preview__col-tax {
  width: 14%;
}

.invoice-preview__table th,
.invoice-preview__table td {
  border: none;
  padding: 4px 4px;
  font-weight: 400;
  text-align: center;
  vertical-align: middle;
  word-break: break-all;
}

.invoice-preview__table thead tr {
  border-bottom: var(--invoice-border);
}

.invoice-preview__table th {
  padding: 6px 2px;
  font-size: 13px;
  font-weight: 400;
  color: var(--invoice-primary);
  white-space: nowrap;
}

.invoice-preview__table td {
  font-size: 12px;
  color: var(--invoice-data);
}

.invoice-preview__table td.is-num {
  font-family: "Arial", "Helvetica Neue", sans-serif;
}

.invoice-preview__item-row td:first-child {
  text-align: left;
  padding-left: 6px;
}

.invoice-preview__blank-row td {
  height: 100px;
}

.invoice-preview__sum-row td {
  border-bottom: var(--invoice-border);
  padding: 4px 4px 6px;
}

.invoice-preview__sum-label {
  text-align: center;
  font-size: 13px;
  letter-spacing: 8px;
  padding-left: 20px !important;
  color: var(--invoice-primary);
}

/* 价税合计 */
.invoice-preview__total {
  display: flex;
  align-items: center;
  border-bottom: var(--invoice-border);
  min-height: 32px;
}

.invoice-preview__total-label {
  width: 130px;
  flex-shrink: 0;
  padding: 6px 8px;
  border-right: var(--invoice-border);
  font-size: 13px;
  color: var(--invoice-primary);
  white-space: nowrap;
}

.invoice-preview__total-words {
  flex: 1;
  padding: 6px 12px;
  font-size: 12px;
  letter-spacing: 1px;
  /* border-right: var(--invoice-border); */
}

.invoice-preview__total-icon {
  display: inline-block;
  margin-right: 6px;
  color: var(--invoice-data);
  font-weight: 700;
}

.invoice-preview__total-num {
  width: 130px;
  flex-shrink: 0;
  padding: 6px 10px;
  text-align: right;
}

.invoice-preview__total-num .is-value {
  font-family: "Arial", sans-serif;
  font-weight: 600;
}

/* 备注 */
.invoice-preview__remark {
  display: flex;
  min-height: 48px;
}

.invoice-preview__remark-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex-shrink: 0;
  border-right: var(--invoice-border);
  font-size: 13px;
  letter-spacing: 6px;
  padding: 4px 0;
  color: var(--invoice-primary);
}

.invoice-preview__remark-content {
  flex: 1;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--invoice-data);
}

.invoice-preview__remark-content p {
  margin: 0;
}

/* 开票人 */
.invoice-preview__issuer {
  margin-top: 8px;
  padding-left: 2px;
}
</style>



// WEBPACK FOOTER //
// src/view/client/personal-center/panels/InvoiceCenter/InvoicePreview.vue